import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormSelect } from '@/components/form/FormSelect'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { StudentSocialLink } from '@/features/student/api/profile.api'
import { useSocialLinkMutations } from '@/features/student/hooks/useProfile'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { humanizeEnum } from '@/utils/format'

const TYPE_OPTIONS = ['linkedin', 'github', 'portfolio', 'website', 'other'].map((value) => ({
  value,
  label: humanizeEnum(value),
}))

const schema = z.object({
  type: z.string().min(1, 'Type is required'),
  url: z.url('Enter a valid URL'),
})

type FormValues = z.infer<typeof schema>

function toFormValues(item?: StudentSocialLink): FormValues {
  return { type: item?.type ?? '', url: item?.url ?? '' }
}

export function SocialLinkSection({ items }: { items: StudentSocialLink[] }) {
  const [editing, setEditing] = useState<StudentSocialLink | 'new' | null>(null)
  const [apiError, setApiError] = useState<unknown>(null)
  const { create, update, remove } = useSocialLinkMutations()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues() })

  useEffect(() => {
    reset(toFormValues(editing === 'new' || editing === null ? undefined : editing))
  }, [editing, reset])

  const isPending = create.isPending || update.isPending

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    const onSettled = { onSuccess: () => setEditing(null), onError: (e: unknown) => setApiError(e) }
    if (editing === 'new') {
      create.mutate(values, onSettled)
    } else if (editing) {
      update.mutate({ id: editing.id, payload: values }, onSettled)
    }
  })

  return (
    <div>
      {items.length === 0 && <p className="text-muted fs-13">No links added yet.</p>}
      {items.map((item) => (
        <div
          key={item.id}
          className="d-flex justify-content-between align-items-center border-bottom py-2"
        >
          <div>
            <span className="badge bg-light text-dark border me-2">{humanizeEnum(item.type)}</span>
            <a href={item.url} target="_blank" rel="noreferrer" className="fs-13">
              {item.url}
            </a>
          </div>
          <div className="d-flex gap-2">
            <Button variant="ghost" onClick={() => setEditing(item)}>
              Edit
            </Button>
            <Button
              variant="danger"
              confirmTitle="Remove this link?"
              onClick={() => remove.mutate(item.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
      <Button variant="secondary" className="mt-3" onClick={() => setEditing('new')}>
        Add Link
      </Button>

      <Modal
        show={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Link' : 'Edit Link'}
      >
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(apiError)}
          generalMessage={apiError ? getErrorMessage(apiError) : null}
        />
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormSelect control={control} name="type" label="Type" options={TYPE_OPTIONS} required />
          <FormInput control={control} name="url" label="URL" type="url" required />
          <Button type="submit" className="w-100" loading={isPending}>
            Save
          </Button>
        </form>
      </Modal>
    </div>
  )
}
